import { X } from "lucide-react"
import { useEffect, useRef, useState } from "react"

import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"

export interface TagsInputProps {
  tags: string[]
  onChange: (tags: string[]) => void
  placeholder?: string
  maxTags?: number
  maxLengthPerTag?: number
  disabled?: boolean
}

export function TagsInput({
  tags,
  onChange,
  placeholder = "Añade y presiona Enter",
  maxTags = 10,
  maxLengthPerTag = 20,
  disabled = false,
}: TagsInputProps) {
  const [value, setValue] = useState("")
  const inputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    // no-op: keep for future enhancements
  }, [])

  const addTag = (raw: string) => {
    if (disabled) return
    const cleaned = raw.trim().toLowerCase()
    if (!cleaned) return
    if (cleaned.length > maxLengthPerTag) return
    if (tags.includes(cleaned)) return
    if (tags.length >= maxTags) return
    onChange([...tags, cleaned])
    setValue("")
  }

  const removeTag = (idx: number) => {
    if (disabled) return
    onChange(tags.filter((_, i) => i !== idx))
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === ",") {
      e.preventDefault()
      addTag(value)
    }
    if (e.key === "Enter") {
      e.preventDefault()
      addTag(value)
    }
    if (e.key === "Backspace" && !value && tags.length > 0) {
      // remove last tag
      removeTag(tags.length - 1)
    }
  }

  return (
    <div className="w-full">
      <div className="mb-2 flex flex-wrap gap-2">
        {tags.map((t, i) => (
          <Badge key={`${t}-${i}`} variant="outline" className="text-xs">
            <span>{t}</span>
            {!disabled && (
              <button
                type="button"
                aria-label={`Eliminar ${t}`}
                onClick={() => removeTag(i)}
                className="text-muted-foreground hover:text-foreground ml-2 inline-flex items-center justify-center"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </Badge>
        ))}
      </div>
      <Input
        ref={inputRef}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        className="h-10"
      />
      <div className="text-muted-foreground mt-1 text-xs">
        {tags.length}/{maxTags} tags • máx {maxLengthPerTag} caracteres por tag
      </div>
    </div>
  )
}
